import { Link } from "react-router-dom";
import { ArrowRight, Handshake, SearchCheck, Landmark } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";

const MODEL = [
  {
    icon: SearchCheck,
    title: "We understand, then we search",
    text: "Every borrower is a profile, not a form. We learn your income, obligations and goal — then scan our partner network for the banks and card issuers whose policy actually fits you.",
  },
  {
    icon: Handshake,
    title: "We make institutions compete",
    text: "As a DSA and facilitator, we present your application to multiple banks and NBFCs. When institutions compete for a well-prepared file, you win on rate, limit and terms.",
  },
  {
    icon: Landmark,
    title: "The bank decides, we stand by",
    text: "Sanctions, card approvals and disbursals always rest with the bank or NBFC — and we never pull or calculate credit scores. Our job is to get your file to a yes, and keep you informed until it's done.",
  },
];

export default function About() {
  useSEO({
    title: "About Loan Salaah — A DSA & Marketplace That Works for Borrowers",
    description: "Loan Salaah is a Direct Selling Agent and facilitator for loans and credit cards. Learn how our model gets borrowers better-structured offers from partner banks and NBFCs.",
  });

  return (
    <div data-testid="about-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">About us</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
            Salaah means counsel. We built a company on it.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-charcoal/80">
            In the old courts, the most valuable thing a mantri offered wasn't gold — it was salaah: honest,
            well-informed advice. Loan Salaah brings that idea to borrowing. We are a Direct Selling Agent (DSA)
            and facilitator — which means we don't lend our own money and we don't issue cards. Instead, we work
            for you: comparing offers across our network of partner banks and NBFCs, matching you to the right
            credit card, and structuring your application.
          </p>
          <p className="mt-4 text-base leading-relaxed text-charcoal/80">
            Because we are compensated by our partners, our guidance costs you nothing. And because we see
            applications across many institutions every day, we know which door is likeliest to open for your
            profile — before a single hard enquiry touches your credit report.
          </p>
        </Reveal>

        <Stagger className="mt-16 grid gap-6 lg:grid-cols-3">
          {MODEL.map((m) => (
            <StaggerItem key={m.title}>
              <div className="h-full rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_28px_60px_rgba(20,40,75,0.14)]" data-testid={`about-model-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-concordia/[0.07] text-concordia">
                  <m.icon className="h-5 w-5" />
                </span>
                <h2 className="mt-6 font-serif text-2xl font-semibold text-concordia">{m.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate">{m.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={0.1}>
          <div className="relative mt-16 overflow-hidden rounded-[2rem] bg-concordia p-10 text-ivory sm:p-12" data-testid="about-cta">
            <div className="noise-overlay" aria-hidden="true" />
            <div className="relative flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
              <div>
                <h2 className="font-serif text-2xl font-semibold sm:text-3xl">Take salaah before you sign.</h2>
                <p className="mt-2 max-w-md text-sm text-ivory/70">One enquiry. An advisor, a comparison, and a structured application — free for borrowers.</p>
              </div>
              <Link to="/apply" data-testid="about-apply-button"
                className="group inline-flex items-center gap-2 rounded-full bg-ivory px-7 py-3.5 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.3)]">
                Apply Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
